type ColumnHelper = ReturnType<NonNullable<typeof window.CBAR>['createColumnHelper']>;
type HtmlHelper = NonNullable<typeof window.CBAR>['html'];

export type CbarDatatableContext = {
  html: HtmlHelper;
  columnHelper: ColumnHelper;
};

export async function waitForCbarDatatable(
  pollIntervalMs = 50,
): Promise<CbarDatatableContext> {
  return new Promise((resolve) => {
    const interval = window.setInterval(() => {
      const html = window.CBAR?.html;
      const columnHelper = window.CBAR?.createColumnHelper?.();

      if (html && columnHelper) {
        window.clearInterval(interval);
        resolve({html, columnHelper});
      }
    }, pollIntervalMs);
  });
}

type DatatableElement = HTMLElement & {
  enableFiltering?: boolean;
  enableColumnVisibility?: boolean;
  enablePagination?: boolean;
  columns?: Array<Record<string, unknown>>;
  data?: unknown[];
  requestUpdate?: () => void;
  getAttribute?: (name: string) => string | null;
  id?: string;
  __cbarProgrammaticData?: boolean;
};

function ensureDatatableDataArray(table: DatatableElement): unknown[] {
  if (Array.isArray(table.data)) {
    return table.data;
  }

  table.data = [];
  return table.data;
}

function hydrateDatatableDataFromDom(table: DatatableElement): unknown[] {
  if (
    table.__cbarProgrammaticData
    && Array.isArray(table.data)
    && table.data.length > 0
  ) {
    return table.data;
  }

  if (Array.isArray(table.data) && table.data.length > 0) {
    return table.data;
  }

  const script = table.id ? document.getElementById(`${table.id}-data`) : null;
  if (script?.textContent) {
    try {
      const parsed = JSON.parse(script.textContent);
      table.data = Array.isArray(parsed) ? parsed : [];
      return table.data;
    } catch {
      table.data = [];
      return table.data;
    }
  }

  const rawAttr = table.getAttribute?.('data');
  if (rawAttr != null && rawAttr.trim() !== '' && rawAttr.trim() !== '[]') {
    try {
      const parsed = JSON.parse(rawAttr);
      table.data = Array.isArray(parsed) ? parsed : [];
      return table.data;
    } catch {
      // Fall through to empty array.
    }
  }

  return ensureDatatableDataArray(table);
}

type DatatableColumn = Record<string, unknown> & {
  header?: string | (() => unknown);
  enableColumnFilter?: boolean;
};

const EMPTY_HEADER_PATTERNS = [
  /^$/,
  /^function\s*\(\)\s*\{\s*return\s*'';\s*\}$/,
  /^function\s*\(\)\s*\{\s*return\s*"";\s*\}$/,
  /^\(\)\s*=>\s*''$/,
  /^\(\)\s*=>\s*""$/,
];

function columnHasEmptyHeader(column: DatatableColumn): boolean {
  if (column.enableColumnFilter === false) {
    return false;
  }
  const header = column.header;
  if (typeof header === 'string') {
    return header.trim() === '';
  }
  if (typeof header === 'function') {
    const source = String(header).replace(/\s+/g, '');
    return EMPTY_HEADER_PATTERNS.some((pattern) => pattern.test(source));
  }
  return false;
}

/** Disable filtering on action/icon columns that intentionally have no header label. */
export function disableEmptyHeaderColumnFilters(columns: DatatableColumn[]): DatatableColumn[] {
  return columns.map((column) => (
    columnHasEmptyHeader(column)
      ? {...column, enableColumnFilter: false}
      : column
  ));
}

function resolveDatatableColumnHeaderLabel(header: DatatableColumn['header']): string | null {
  if (typeof header === 'string') {
    const trimmed = header.trim();
    return trimmed || null;
  }
  if (typeof header === 'function') {
    try {
      const result = header();
      if (typeof result === 'string') {
        const trimmed = result.trim();
        return trimmed || null;
      }
    } catch {
      // Some header renderers require TanStack context.
    }
  }
  return null;
}

/** Resolve function headers to plain strings so column visibility shows human labels. */
export function normalizeDatatableColumns(columns: DatatableColumn[]): DatatableColumn[] {
  let changed = false;
  const normalized = columns.map((column) => {
    if (!column || typeof column !== 'object' || typeof column.header === 'string') {
      return column;
    }

    const label = resolveDatatableColumnHeaderLabel(column.header);
    if (label) {
      changed = true;
      return {...column, header: label};
    }

    return column;
  });

  return changed ? normalized : columns;
}

/** Assign columns and apply shared PaymentNGN datatable conventions. */
export function assignDatatableColumns(
  table: DatatableElement,
  columns: DatatableColumn[],
): void {
  hydrateDatatableDataFromDom(table);
  table.columns = disableEmptyHeaderColumnFilters(normalizeDatatableColumns(columns));
  syncDatatableColumnFiltering(table);
}

/** Assign HTML column defs and refresh row data so every cell re-renders. */
export function assignHtmlDatatableColumns(
  table: DatatableElement,
  columns: DatatableColumn[],
): void {
  hydrateDatatableDataFromDom(table);
  const normalized = disableEmptyHeaderColumnFilters(normalizeDatatableColumns(columns));
  // Always assign so TanStack/Lit pick up async column defs after data hydration.
  table.columns = normalized;
  if (Array.isArray(table.data)) {
    table.data = [...table.data];
  }
  table.requestUpdate?.();
  syncDatatableColumnFiltering(table);
}

/** Update datatable row data without rebuilding column definitions. */
export function setDatatableData(table: DatatableElement, data: unknown[]): void {
  const rows = Array.isArray(data) ? data : [];
  table.__cbarProgrammaticData = rows.length > 0;
  table.data = rows;
  // Drop static markup so all-imports hydrateDatatableData cannot reset async rows to [].
  table.removeAttribute?.('data');
  table.requestUpdate?.();
  syncDatatableColumnFiltering(table);
}

export {
  configureServerSideDatatable,
  createServerPagination,
  setServerSideDatatableData,
} from './common/server-pagination';

const PAGE_FILTER_SELECTORS = '.cbar-filter-card, form.cbar-filter-form, [data-datatable-page-filters]';
const DEFAULT_MIN_ROWS_FOR_COLUMN_FILTERING = 11;

function parseMinFilterRows(table: DatatableElement): number {
  const raw = table.dataset.minFilterRows;
  if (raw == null || raw.trim() === '') {
    return DEFAULT_MIN_ROWS_FOR_COLUMN_FILTERING;
  }

  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_MIN_ROWS_FOR_COLUMN_FILTERING;
}

function pageHasDatatableFilterSection(table: DatatableElement): boolean {
  const scope = table.closest('.admin-page, .content-wrapper, main, body') ?? document.body;
  return scope.querySelector(PAGE_FILTER_SELECTORS) !== null;
}

/** Turn on built-in column filtering when row count and page layout allow it. */
export function syncDatatableColumnFiltering(
  table: DatatableElement,
  options?: {minRows?: number; optIn?: boolean},
): void {
  const optedIn = options?.optIn ?? table.hasAttribute('enable-filtering');
  if (!optedIn) {
    return;
  }

  const minRows = options?.minRows ?? parseMinFilterRows(table);
  const data = hydrateDatatableDataFromDom(table);
  const shouldFilter = data.length >= minRows && !pageHasDatatableFilterSection(table);

  if (table.enableFiltering !== shouldFilter) {
    table.enableFiltering = shouldFilter;
    table.requestUpdate?.();
  }
}

/** Opt a table into column filtering when row count and page layout allow it. */
export function enableDatatableFiltering(table: HTMLElement): void {
  syncDatatableColumnFiltering(table as DatatableElement, {optIn: true});
}

const MOBILE_DATATABLE_MEDIA_QUERY = '(max-width: 768px)';

function applyMobileColumnVisibility(table: DatatableElement, enabled: boolean): void {
  if (table.enableColumnVisibility === enabled) {
    return;
  }
  table.enableColumnVisibility = enabled;
  table.requestUpdate?.();
}

function syncDatatableColumnVisibilityForViewport(enabled: boolean): void {
  document.querySelectorAll('cbar-datatable').forEach((table) => {
    applyMobileColumnVisibility(table as DatatableElement, enabled);
  });
}

function syncDatatableColumnVisibility(table: DatatableElement): void {
  if (typeof window.matchMedia !== 'function') {
    return;
  }
  applyMobileColumnVisibility(
    table,
    window.matchMedia(MOBILE_DATATABLE_MEDIA_QUERY).matches,
  );
}

/** Show column visibility toggles on phone-sized viewports for every cbar-datatable. */
export function setupMobileDatatableColumnVisibility(): void {
  if (typeof window.matchMedia !== 'function') {
    return;
  }

  const mediaQuery = window.matchMedia(MOBILE_DATATABLE_MEDIA_QUERY);

  syncDatatableColumnVisibilityForViewport(mediaQuery.matches);
  mediaQuery.addEventListener('change', () => {
    syncDatatableColumnVisibilityForViewport(mediaQuery.matches);
  });

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }
        if (node.tagName === 'CBAR-DATATABLE') {
          syncDatatableColumnVisibility(node as DatatableElement);
          continue;
        }
        node.querySelectorAll('cbar-datatable').forEach((table) => {
          syncDatatableColumnVisibility(table as DatatableElement);
        });
      }
    }
  });

  observer.observe(document.body, {childList: true, subtree: true});
}

export function linkCell(
  html: HtmlHelper,
  value: {value?: string; url?: string} | null | undefined,
): unknown {
  if (value?.url) {
    return html`<a href="${value.url}">${value.value ?? ''}</a>`;
  }

  return value?.value ?? '';
}
